import React from 'react';
import { Modal, Button, Typography, Descriptions, List, Tag, Tooltip, Card } from 'antd';
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  ClockCircleOutlined,
  TrophyOutlined,
  HeartOutlined,
  ExperimentOutlined,
  BookOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { getLifestyleDisplayLabel } from '../utils/lifestyleDisplayMapping';

const { Title, Text, Paragraph } = Typography;

const OrderDetailsModal = ({ 
  visible, 
  onClose, 
  orderData, 
  categoryConfig = {} 
}) => {
  if (!orderData || !orderData.originalEventDetails) {
    return (
      <Modal
        title="Order Details"
        open={visible}
        onCancel={onClose}
        footer={[<Button key="close" onClick={onClose}>Close</Button>]}
        width={800}
      >
        <Text type="secondary">No order details available</Text>
      </Modal>
    );
  }

  const { originalEventDetails } = orderData;
  const isLifestyleOrder = originalEventDetails.orderType === 'Lifestyle';
  const isCardBasedOrder = isLifestyleOrder && originalEventDetails.cardId;

  // Helper function to render evidence level badge
  const renderEvidenceLevel = () => {
    if (!originalEventDetails.evidenceLevel) return null;
    
    return (
      <Tooltip title={originalEventDetails.evidenceLevel.description}>
        <Tag 
          color={originalEventDetails.evidenceLevel.color}
          style={{ fontWeight: 'bold', marginBottom: 8 }}
        >
          <ExperimentOutlined style={{ marginRight: 4 }} />
          Level {originalEventDetails.evidenceLevel.level}
        </Tag>
      </Tooltip>
    );
  };

  // Helper function to render difficulty badge
  const renderDifficulty = () => {
    if (!originalEventDetails.difficulty) return null;
    
    return (
      <Tag 
        color={originalEventDetails.difficulty.color}
        style={{ fontWeight: 'bold', marginBottom: 8 }}
      >
        <TrophyOutlined style={{ marginRight: 4 }} />
        {originalEventDetails.difficulty.level}
      </Tag>
    );
  };

  // Helper function to render time commitment badge
  const renderTimeCommitment = () => {
    if (!originalEventDetails.timeCommitment) return null;
    
    return (
      <Tag 
        color={originalEventDetails.timeCommitment.color}
        style={{ fontWeight: 'bold', marginBottom: 8 }}
      >
        <ClockCircleOutlined style={{ marginRight: 4 }} />
        {originalEventDetails.timeCommitment.label}
      </Tag>
    );
  };

  // Helper function to render basic order information
  const renderBasicInfo = () => {
    const items = [
      {
        key: 'type',
        label: 'Order Type',
        children: orderData.originalEventType === 'appointment' ? 'Appointment' : (originalEventDetails.orderType || 'N/A')
      },
      {
        key: 'title',
        label: 'Title',
        children: originalEventDetails.title || orderData.name || 'N/A'
      },
      {
        key: 'category',
        label: 'Category',
        children: (
          <span style={{ 
            color: categoryConfig[originalEventDetails.categoryName?.toLowerCase().replace(/ /g, '_') || 'default']?.color 
          }}>
            ● {categoryConfig[originalEventDetails.categoryName?.toLowerCase().replace(/ /g, '_') || 'default']?.label || originalEventDetails.categoryName}
          </span>
        )
      },
      {
        key: 'status',
        label: 'Status',
        children: <Tag color={getStatusColor(originalEventDetails.status)}>{originalEventDetails.status || 'N/A'}</Tag>
      },
      {
        key: 'startDate',
        label: 'Start Date',
        children: moment(orderData.startDate).format('MMMM Do YYYY, h:mm a')
      }
    ];

    if (orderData.endDate && orderData.endDate !== orderData.startDate) {
      items.push({
        key: 'endDate',
        label: 'End Date',
        children: moment(orderData.endDate).format('MMMM Do YYYY, h:mm a')
      });
    }

    if (originalEventDetails.duration) {
      items.push({
        key: 'duration',
        label: 'Duration',
        children: originalEventDetails.duration
      });
    }

    if (originalEventDetails.providerName) {
      items.push({
        key: 'provider',
        label: 'Provider',
        children: originalEventDetails.providerName
      });
    }

    return <Descriptions bordered column={1} items={items} size="small" />;
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'resulted':
        return 'green';
      case 'active':
      case 'in progress':
        return 'blue';
      case 'pending':
        return 'orange';
      case 'cancelled':
        return 'red';
      default:
        return 'default';
    }
  };

  // Helper function to render SMART goals
  const renderSmartGoals = () => {
    if (!originalEventDetails.smartGoals || originalEventDetails.smartGoals.length === 0) return null;

    return (
      <Card size="small" style={{ marginTop: 16 }}>
        <Title level={5} style={{ margin: 0, marginBottom: 12, color: '#52c41a' }}>
          <CheckCircleOutlined style={{ marginRight: 8 }} />
          SMART Goals
        </Title>
        <List
          size="small"
          dataSource={originalEventDetails.smartGoals}
          renderItem={(goal, index) => (
            <List.Item style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 12, marginTop: 2 }} />
                <Text style={{ flex: 1 }}><strong>{index + 1}.</strong> {goal}</Text>
              </div>
            </List.Item>
          )}
        />
      </Card>
    );
  };

  // Helper function to render clinical rationale
  const renderClinicalRationale = () => {
    if (!originalEventDetails.clinicalRationale) return null;

    return (
      <Card size="small" style={{ marginTop: 16, backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}>
        <Title level={5} style={{ margin: 0, marginBottom: 12, color: '#389e0d' }}>
          <BookOutlined style={{ marginRight: 8 }} />
          Clinical Rationale
        </Title>
        <Paragraph style={{ margin: 0, color: '#389e0d' }}>
          {originalEventDetails.clinicalRationale}
        </Paragraph>
      </Card>
    );
  };

  // Helper function to render contraindications
  const renderContraindications = () => {
    if (!originalEventDetails.contraindications || originalEventDetails.contraindications.length === 0) return null;

    return (
      <Card size="small" style={{ marginTop: 16, backgroundColor: '#fff2e8', border: '1px solid #ffbb96' }}>
        <Title level={5} style={{ margin: 0, marginBottom: 12, color: '#d4380d' }}>
          <ExclamationCircleOutlined style={{ marginRight: 8 }} />
          Contraindications
        </Title>
        <List
          size="small"
          dataSource={originalEventDetails.contraindications}
          renderItem={(item) => (
            <List.Item style={{ padding: '4px 0', borderBottom: 'none' }}>
              <Text style={{ color: '#d4380d' }}>• {item}</Text>
            </List.Item>
          )}
        />
      </Card>
    );
  };

  // Helper function to render monitoring parameters
  const renderMonitoring = () => {
    if (!originalEventDetails.monitoring || originalEventDetails.monitoring.length === 0) return null;

    return (
      <Card size="small" style={{ marginTop: 16, backgroundColor: '#e6f7ff', border: '1px solid #91d5ff' }}>
        <Title level={5} style={{ margin: 0, marginBottom: 12, color: '#0958d9' }}>
          <EyeOutlined style={{ marginRight: 8 }} />
          Monitoring Parameters
        </Title>
        <List
          size="small"
          dataSource={originalEventDetails.monitoring}
          renderItem={(item) => (
            <List.Item style={{ padding: '4px 0', borderBottom: 'none' }}>
              <Text style={{ color: '#0958d9' }}>• {item}</Text>
            </List.Item>
          )}
        />
      </Card>
    );
  };

  // Helper function to render expected outcomes
  const renderExpectedOutcomes = () => {
    if (!originalEventDetails.expectedOutcomes) return null;

    return (
      <Card size="small" style={{ marginTop: 16, backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}>
        <Title level={5} style={{ margin: 0, marginBottom: 12, color: '#389e0d' }}>
          <HeartOutlined style={{ marginRight: 8 }} />
          Expected Outcomes
        </Title>
        <Paragraph style={{ margin: 0, color: '#389e0d' }}>
          {originalEventDetails.expectedOutcomes}
        </Paragraph>
      </Card>
    );
  };

  // Helper function to render target parameters
  const renderTargetParameters = () => {
    if (!originalEventDetails.targetParameters || originalEventDetails.targetParameters.length === 0) return null;

    return (
      <Card size="small" style={{ marginTop: 16, backgroundColor: '#fff7e6', border: '1px solid #ffd591' }}>
        <Title level={5} style={{ margin: 0, marginBottom: 12, color: '#d46b08' }}>
          <TrophyOutlined style={{ marginRight: 8 }} />
          Target Parameters
        </Title>
        <List
          size="small"
          dataSource={originalEventDetails.targetParameters}
          renderItem={(item) => {
            const displayName = getLifestyleDisplayLabel(item);
            return (
              <List.Item style={{ padding: '4px 0', borderBottom: 'none' }}>
                <Tooltip title={`Standard parameter: ${item}`} placement="top">
                  <Text style={{ color: '#d46b08' }}>• {displayName}</Text>
                </Tooltip>
              </List.Item>
            );
          }}
        />
      </Card>
    );
  };

  // Helper function to render citations
  const renderCitations = () => {
    if (!originalEventDetails.citations || originalEventDetails.citations.length === 0) return null;

    return (
      <Card size="small" style={{ marginTop: 16, backgroundColor: '#f9f0ff', border: '1px solid #d3adf7' }}>
        <Title level={5} style={{ margin: 0, marginBottom: 12, color: '#722ed1' }}>
          <BookOutlined style={{ marginRight: 8 }} />
          Scientific Citations
        </Title>
        <List
          size="small"
          dataSource={originalEventDetails.citations}
          renderItem={(citation, index) => (
            <List.Item style={{ padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
              <Text style={{ color: '#722ed1', fontSize: '12px' }}>
                <strong>[{index + 1}]</strong> {citation}
              </Text>
            </List.Item>
          )}
        />
      </Card>
    );
  };

  return (
    <Modal
      title={
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Order Details
          </Title>
          {isCardBasedOrder && (
            <div style={{ marginTop: 8 }}>
              {renderEvidenceLevel()}
              {renderDifficulty()}
              {renderTimeCommitment()}
            </div>
          )}
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>
      ]}
      width={1000}
      style={{ top: 20 }}
    >
      <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {renderBasicInfo()}
        
        {originalEventDetails.notes && (
          <Card size="small" style={{ marginTop: 16 }}>
            <Title level={5} style={{ margin: 0, marginBottom: 8 }}>Notes</Title>
            <Text>{originalEventDetails.notes}</Text>
          </Card>
        )}

        {isCardBasedOrder && (
          <>
            {renderSmartGoals()}
            {renderClinicalRationale()}
            {renderTargetParameters()}
            {renderContraindications()}
            {renderMonitoring()}
            {renderExpectedOutcomes()}
            {renderCitations()}
          </>
        )}

        {/* Additional order type specific information */}
        {originalEventDetails.orderType === 'Laboratory' && originalEventDetails.testName && (
          <Card size="small" style={{ marginTop: 16 }}>
            <Title level={5} style={{ margin: 0, marginBottom: 8 }}>Laboratory Details</Title>
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Test Name">{originalEventDetails.testName}</Descriptions.Item>
              {originalEventDetails.specimenType && (
                <Descriptions.Item label="Specimen Type">{originalEventDetails.specimenType}</Descriptions.Item>
              )}
              {originalEventDetails.labName && (
                <Descriptions.Item label="Laboratory">{originalEventDetails.labName}</Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        )}

        {originalEventDetails.orderType === 'Radiology' && originalEventDetails.studyDescription && (
          <Card size="small" style={{ marginTop: 16 }}>
            <Title level={5} style={{ margin: 0, marginBottom: 8 }}>Radiology Details</Title>
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Study">{originalEventDetails.studyDescription}</Descriptions.Item>
              {originalEventDetails.reasonForStudy && (
                <Descriptions.Item label="Reason">{originalEventDetails.reasonForStudy}</Descriptions.Item>
              )}
              {originalEventDetails.imagingCenter && (
                <Descriptions.Item label="Imaging Center">{originalEventDetails.imagingCenter}</Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        )}
      </div>
    </Modal>
  );
};

export default OrderDetailsModal;
